import { logger } from '../utils/logger';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTutorial } from '../contexts/TutorialContext';
import { TutorialStep } from '../config/tutorials';
import '../styles/Tutorial.css';

const TutorialOverlay: React.FC = () => {
  const {
    activeTutorial,
    currentStep: stepIndex,
    isActive,
    nextStep,
    previousStep,
    skipTutorial
  } = useTutorial();

  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);
  const [modalPosition, setModalPosition] = useState<{ top: number; left: number } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const currentStepData: TutorialStep | undefined = activeTutorial?.steps[stepIndex];

  const updatePositions = useCallback(() => {
    if (!currentStepData) return;

    if (!currentStepData.targetSelector) {
      setSpotlightRect(null);
      setModalPosition(null);
      return;
    }

    try {
      const targetElement = document.querySelector(currentStepData.targetSelector);

      if (!targetElement) {
        logger.warn(`Tutorial target not found: ${currentStepData.targetSelector}`);
        setSpotlightRect(null);
        setModalPosition(null);
        return;
      }

      const rect = targetElement.getBoundingClientRect();
      setSpotlightRect(rect);

      const position = currentStepData.position || 'bottom';
      const modalWidth = 480;
      const modalHeight = 300;
      const gap = 20;

      let top = 0;
      let left = 0;

      switch (position) {
        case 'top':
          top = rect.top - modalHeight - gap;
          left = rect.left + rect.width / 2 - modalWidth / 2;
          break;
        case 'bottom':
          top = rect.bottom + gap;
          left = rect.left + rect.width / 2 - modalWidth / 2;
          break;
        case 'left':
          top = rect.top + rect.height / 2 - modalHeight / 2;
          left = rect.left - modalWidth - gap;
          break;
        case 'right':
          top = rect.top + rect.height / 2 - modalHeight / 2;
          left = rect.right + gap;
          break;
        case 'center':
          top = window.innerHeight / 2 - modalHeight / 2;
          left = window.innerWidth / 2 - modalWidth / 2;
          break;
      }

      top = Math.max(20, Math.min(top, window.innerHeight - modalHeight - 20));
      left = Math.max(20, Math.min(left, window.innerWidth - modalWidth - 20));

      setModalPosition({ top, left });
    } catch (error) {
      logger.error('Error calculating tutorial positions:', error);
      setSpotlightRect(null);
      setModalPosition(null);
    }
  }, [currentStepData]);

  useEffect(() => {
    if (!isActive) return;
    updatePositions();
    const handleResize = () => updatePositions();
    window.addEventListener('resize', handleResize);
    const timer = setTimeout(updatePositions, 100);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [isActive, stepIndex, currentStepData, updatePositions]);

  useEffect(() => {
    if (!isActive) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'Enter':
          e.preventDefault();
          nextStep();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          previousStep();
          break;
        case 'Escape':
          e.preventDefault();
          skipTutorial();
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, nextStep, previousStep, skipTutorial]);

  if (!isActive || !activeTutorial || !currentStepData) {
    return null;
  }

  const progress = ((stepIndex + 1) / activeTutorial.steps.length) * 100;
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === activeTutorial.steps.length - 1;
  const isCentered = !currentStepData.targetSelector || currentStepData.position === 'center';

  return (
    <div className="tutorial-overlay" ref={overlayRef}>
      <div className="tutorial-backdrop" onClick={skipTutorial} />

      {spotlightRect && (
        <div
          className="tutorial-spotlight"
          style={{
            top: spotlightRect.top - (currentStepData.highlightPadding || 8),
            left: spotlightRect.left - (currentStepData.highlightPadding || 8),
            width: spotlightRect.width + (currentStepData.highlightPadding || 8) * 2,
            height: spotlightRect.height + (currentStepData.highlightPadding || 8) * 2
          }}
        />
      )}

      <div
        className={`tutorial-content-modal ${isCentered ? 'tutorial-modal-center' : ''}`}
        style={
          isCentered
            ? undefined
            : modalPosition
            ? { top: modalPosition.top, left: modalPosition.left }
            : undefined
        }
      >
        <div className="tutorial-modal-header">
          <h3 className="tutorial-modal-title">{currentStepData.title}</h3>
          <button
            className="tutorial-modal-close"
            onClick={skipTutorial}
            aria-label="Close tutorial"
          >
            ×
          </button>
        </div>

        <div className="tutorial-progress">
          <span className="tutorial-progress-text">
            Step {stepIndex + 1} of {activeTutorial.steps.length}
          </span>
          <div className="tutorial-progress-bar">
            <div className="tutorial-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="tutorial-step-dots">
          {activeTutorial.steps.map((_, index) => (
            <div
              key={index}
              className={`tutorial-step-dot ${
                index === stepIndex ? 'active' : index < stepIndex ? 'completed' : ''
              }`}
            />
          ))}
        </div>

        <div className="tutorial-modal-content">{currentStepData.content}</div>

        {currentStepData.interactionHint && (
          <div className="tutorial-interaction-hint">{currentStepData.interactionHint}</div>
        )}

        <div className="tutorial-modal-footer">
          <button className="tutorial-btn tutorial-btn-skip" onClick={skipTutorial}>
            Skip
          </button>
          <button
            className="tutorial-btn tutorial-btn-secondary"
            onClick={previousStep}
            disabled={isFirstStep}
          >
            Previous
          </button>
          <button className="tutorial-btn tutorial-btn-primary" onClick={nextStep}>
            {isLastStep ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TutorialOverlay;
